import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MSG } from "../constants/toastMessages";
import noData from "../assets/images/no-data.png";
import Loader from "../assets/images/loader.gif";
import moment from 'moment';
import { apiCallingPost } from '../api/services';
import { toast } from 'react-toastify';
import Pagination from "react-js-pagination";
import { FiSearch } from "react-icons/fi";
import DatePicker from "react-datepicker";
import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowDown, MdOutlineStayCurrentLandscape, MdOutlineStayCurrentPortrait, MdOutlineEmail, MdOutlineLocalPhone, MdOutlineBusinessCenter, MdOutlineChatBubbleOutline } from "react-icons/md";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { FaImage, FaVideo } from "react-icons/fa";
import MediaPreview from '../components/MediaPreview';
import RequestStatusTabs from '../components/RequestStatusTabs';
import DecisionSelectionBar from '../components/DecisionSelectionBar';
import ExpandableText from '../components/ExpandableText';
import UsageChips from '../components/UsageChips';
import DecisionMenu from '../components/DecisionMenu';
import { LuSearchX } from "react-icons/lu";
import { IoCloseOutline } from "react-icons/io5";
import { groupRequestRows, formatRequestDate, assetCountLabel, requestGroupTitle, statusBadgeClass, formatFileSize, GROUP_STATUS_LABEL, ROW_STATUS_LABEL } from '../utils/requestGroups';

const REASON_MAX = 120;

// Approver's grouped view (#9 minimal package): one card per request
// (submission) with status tabs, per-asset decisions that stay editable,
// a mandatory reason on every rejection, and group-scoped bulk actions.
function VideoDownloadRequest() {
  const [groups, setGroups] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [tab, setTab] = useState("");
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [itemsCountPerPage, setItemsCountPerPage] = useState(10);
  const [expanded, setExpanded] = useState({});
  // Selection is scoped to ONE request at a time (bulk reject shares one reason).
  const [selected, setSelected] = useState({ groupKey: null, ids: [] });
  // Filters (applied on Search / Clear, like before)
  const [startDate, setStartDate] = useState("");
  const [fileType, setFileType] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [applied, setApplied] = useState({ type: "", textInput: "", requestedDate: "" });
  const [ImagePopup, setImagePopupFlag] = useState(false);
  const [popupSelectData, setPopupSelectData] = useState([]);
  // Reject modal: which rows, and the typed reason
  const [rejectTarget, setRejectTarget] = useState(null);   // { ids: [], names: [] }
  const [rejectText, setRejectText] = useState("");
  // Requester-details popup (opened by clicking the requester's name).
  const [requesterPopup, setRequesterPopup] = useState(false);
  const [requesterDetails, setRequesterDetails] = useState(null);
  const [requesterRequest, setRequesterRequest] = useState(null);
  const [requesterLoading, setRequesterLoading] = useState(false);
  const [saveFlag, setSaveFlag] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        cancelPopup();
        setRequesterPopup(false);
        setRejectTarget(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // keepExpanded: a refetch after a decision must not collapse the card the
  // approver is working in; page/tab/filter changes start with the first card open.
  const requestVideo = useCallback(async (page, perPage, status, filters, { keepExpanded = false } = {}) => {
    setLoading(true);
    try {
      const response = await apiCallingPost("api/video/VideoDownloadRequestList", {
        grouped: true,
        max: Number(perPage),
        offset: (page - 1) * Number(perPage),
        type: filters.type,
        textInput: filters.textInput,
        requestedDate: filters.requestedDate,
        status: status || "",
      });
      const { data } = response;
      if (data.success === "1") {
        const g = groupRequestRows(data.result);
        const total = Number(data.totalCount) || 0;
        setGroups(g);
        setStatusCounts(data.statusCounts || {});
        setTotalCount(total);
        if (!keepExpanded) setExpanded(g.length ? { [g[0].groupKey]: true } : {});
        return { groups: g, totalCount: total };
      }
      setGroups([]);
      setStatusCounts({});
      setTotalCount(0);
      toast.error(data.errorMessage || MSG.couldNotProcess);
    } catch (error) {
      console.error("Error fetching download requests:", error);
      setGroups([]);
      setStatusCounts({});
      setTotalCount(0);
      toast.error(MSG.couldNotProcess);
    } finally {
      setSelected({ groupKey: null, ids: [] });
      setLoading(false);
    }
    return { groups: [], totalCount: 0 };
  }, []);

  useEffect(() => {
    requestVideo(activePage, itemsCountPerPage, tab, applied);
  }, [requestVideo, activePage, itemsCountPerPage, tab, applied]);

  // After a decision: same page/tab/filters, cards stay as they were. If the
  // decided request no longer belongs to the selected tab, say so.
  const refreshAfterDecision = async (groupKey) => {
    const { groups: g, totalCount: total } = await requestVideo(activePage, itemsCountPerPage, tab, applied, { keepExpanded: true });
    if (tab && groupKey && !g.some((x) => x.groupKey === groupKey)) toast.info(MSG.requestLeftTab);
    // The decided request may have been the last one on this page of the
    // tab. Snap back to the last page that still has requests rather than
    // leaving the approver on an empty page (the pager only renders with rows).
    if (g.length === 0 && activePage > 1) {
      setActivePage(Math.max(1, Math.ceil(total / itemsCountPerPage)));
    }
  };
  const changeTab = (next) => { setTab(next); setActivePage(1); };
  const changePerPage = (n) => { setItemsCountPerPage(n); setActivePage(1); };
  // Accordion: one request open at a time; clicking the open one closes it.
  // The bulk bar acts on the selection, not on what is visible, so a selection
  // left behind in a request that just collapsed would let an approver decide
  // rows they can no longer see. It goes with the card.
  const toggleGroup = (key) => {
    const willOpen = !expanded[key];
    setExpanded(willOpen ? { [key]: true } : {});
    if (selected.groupKey && !(willOpen && selected.groupKey === key)) clearSelection();
  };

  const runSearch = () => {
    if (fileType && !searchInput) {
      toast.warn(MSG.searchTextRequired);
      return;
    }
    setActivePage(1);
    setApplied({
      type: fileType,
      textInput: searchInput,
      requestedDate: startDate ? moment(startDate).format('YYYY-MM-DD') : "",
    });
  };
  const clearSearch = () => {
    setFileType(""); setSearchInput(""); setStartDate("");
    setActivePage(1);
    setApplied({ type: "", textInput: "", requestedDate: "" });
  };
  const searchOnChange = (e) => {
    setFileType(e.target.value);
    if (e.target.value === "") setSearchInput("");
  };

  // ---- decisions -----------------------------------------------------------
  // An approval grants every format (Original + MP4 renditions); the
  // requester picks the variant at download time.
  const statusChangeFn = async (ids, status, rejectionReason = "", groupKey = null) => {
    const request = ids.map((id) => ({ requestVideoId: id, download_mp4: true, download_original: true }));
    setSaveFlag(true);
    try {
      const { data } = await apiCallingPost("api/video/statusChangeVideoDownload", {
        request, status, rejectionReason, user_id: "",
      });
      if (data.success === "1") {
        setRejectTarget(null);
        setRejectText("");
        toast.success(status === "APPROVED" ? MSG.mediaApproved : status === "REJECTED" ? MSG.mediaRejected : MSG.mediaStatusUpdated);
        refreshAfterDecision(groupKey);
      } else {
        toast.error(data.errorMessage || MSG.couldNotProcess);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(MSG.couldNotProcess);
    } finally {
      setTimeout(() => setSaveFlag(false), 700);
    }
  };

  const openReject = (rows) => {
    setRejectText("");
    setRejectTarget({ ids: rows.map((r) => r.requestVideoDownloadId), names: rows.map((r) => r.videoName), groupKey: rows[0]?.groupKey ?? null });
  };
  const confirmReject = () => {
    const reason = rejectText.trim();
    if (!reason) {
      toast.error(MSG.rejectReasonRequired);
      return;
    }
    statusChangeFn(rejectTarget.ids, "REJECTED", reason, rejectTarget.groupKey);
  };

  // Per-row select: the current status is shown; picking another applies it.
  const onRowStatusChange = (row, value) => {
    if (value === row.status) return;
    if (value === "REJECTED") openReject([row]);
    else statusChangeFn([row.requestVideoDownloadId], value, "", row.groupKey);
  };

  // ---- selection (one request at a time) ------------------------------------
  // Any row can be selected; a bulk decision only touches rows whose status
  // would actually change (re-sending an unchanged decision would re-email
  // the requester).
  const isSelected = (id) => selected.ids.includes(id);
  const toggleRow = (group, id, checked) => {
    setSelected((prev) => {
      const sameGroup = prev.groupKey === group.groupKey;
      const base = sameGroup ? prev.ids : [];
      return { groupKey: group.groupKey, ids: checked ? Array.from(new Set([...base, id])) : base.filter((x) => x !== id) };
    });
  };
  const toggleGroupAll = (group, checked) => {
    setSelected(checked
      ? { groupKey: group.groupKey, ids: group.rows.map((r) => r.requestVideoDownloadId) }
      : { groupKey: null, ids: [] });
  };
  const selectedRows = useMemo(() => {
    const g = groups.find((x) => x.groupKey === selected.groupKey);
    return g ? g.rows.filter((r) => selected.ids.includes(r.requestVideoDownloadId)) : [];
  }, [groups, selected]);
  const approveSelected = () => {
    if (selectedRows.length < 1) { toast.error(MSG.selectToApprove); return; }
    const rows = selectedRows.filter((r) => r.status !== "APPROVED");
    if (rows.length < 1) { toast.info(MSG.alreadyApprovedSelection); return; }
    statusChangeFn(rows.map((r) => r.requestVideoDownloadId), "APPROVED", "", selected.groupKey);
  };
  const rejectSelected = () => {
    if (selectedRows.length < 1) { toast.error(MSG.selectToReject); return; }
    const rows = selectedRows.filter((r) => r.status !== "REJECTED");
    if (rows.length < 1) { toast.info(MSG.alreadyRejectedSelection); return; }
    openReject(rows);
  };
  const clearSelection = () => setSelected({ groupKey: null, ids: [] });

  // ---- popups --------------------------------------------------------------
  const popupTagShow = (data) => {
    setImagePopupFlag(true);
    setPopupSelectData(data);
    document.body.style.overflow = 'hidden';
  };
  const cancelPopup = () => {
    setImagePopupFlag(false);
    document.body.style.overflow = 'auto';
  };
  // Fetch the requester's full details (reuses userList filtered by id) for the popup.
  const openRequesterPopup = async (group) => {
    setRequesterPopup(true);
    setRequesterDetails(null);
    setRequesterRequest({
      projectName: group.projectName ?? null,
      projectBrief: group.projectBrief ?? null,
      usageTypes: group.usageTypes ?? null,
    });
    setRequesterLoading(true);
    try {
      const res = await apiCallingPost("api/user/userList", { userId: group.userId, userName: "", offset: 0, max: 10, roleId: "" });
      if (res.data.success === "1" && res.data.result && res.data.result.length) {
        setRequesterDetails(res.data.result[0]);
      } else {
        setRequesterDetails(null);
      }
    } catch (err) {
      toast.error(MSG.loadUserDetailsFailed);
    } finally {
      setRequesterLoading(false);
    }
  };

  return (
    <>
      <div className='admin-toolbar tall'>
        <div className='container'>
          <div className='row w-100 g-3 video-list-filters-grid'>
            <div className='col-md-3 filter-column'>
              <div className='search-div'>
                <select className='search-input' value={fileType} aria-label="Search field" onChange={searchOnChange}>
                  <option value="">Select type</option>
                  <option value="Project Name">Project Name</option>
                  {/* labels say "asset" (photos too); the VALUES are the literal strings
                      the BE's requestListFilterSql matches on — do not rename them */}
                  <option value="Video Name">Asset Name</option>
                  <option value="Video Requested By Name">Requested By</option>
                  <option value="Event Name">Event Name</option>
                  <option value="Locations">Location Name</option>
                </select>
              </div>
            </div>
            <div className='col-md-3 filter-column'>
              <div className='search-div'>
                <input type='text' disabled={fileType === ""} value={searchInput} className='search-input search-input-default' placeholder='Search text'
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                />
              </div>
            </div>
            <div className='col-md-3 filter-column'>
              <div className='search-div'>
                <DatePicker selected={startDate} className='search-input search-input-picker' wrapperClassName='w-100' placeholderText="Date"
                  onChange={(date) => { setStartDate(date) }}
                  calendarClassName="custom-calendar"
                  dateFormat="dd/MM/yyyy"
                  renderCustomHeader={({
                    date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled,
                  }) => (
                    <div className="custom-header">
                      <div className="custom-header-top">
                        <div className="day">
                          {date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </div>
                      </div>
                      <div className="custom-month-nav">
                        <span className="month-year">
                          {date.toLocaleString("default", { month: "long" })} {date.getFullYear()}
                        </span>
                        <div>
                          <span onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className="btn-picker">
                            <MdKeyboardArrowLeft color="#fff" size={25} />
                          </span>
                          <span onClick={increaseMonth} disabled={nextMonthButtonDisabled} className="btn-picker">
                            <MdOutlineKeyboardArrowRight color="#fff" size={25} />
                          </span>
                        </div>
                      </div>
                    </div>
                  )} />
              </div>
            </div>
            <div className='col-md-3 filter-column'>
              <div className='filter-actions-row'>
                <button type="button" className='search-icon-btn' onClick={runSearch}>
                  <FiSearch /> Search
                </button>
                <button type="button" className='search-icon-btn search-icon-clear' onClick={clearSearch}>
                  <LuSearchX /> Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='container mt-3'>
        <RequestStatusTabs value={tab} counts={statusCounts} onChange={changeTab} />

        {loading ? (
          <div className="noData d-flex flex-column">
            <img src={Loader} alt="loading" className="img-fluid" width="150" height="150" />
          </div>
        ) : groups.length > 0 ? (
          <>
            <div className="request-groups">
              {groups.map((g) => {
                const open = !!expanded[g.groupKey];
                const allSelected = selected.groupKey === g.groupKey && g.rows.every((r) => selected.ids.includes(r.requestVideoDownloadId));
                return (
                  <div className="request-group request-group--admin" key={g.groupKey}>
                    <div className="request-group__header" role="button" tabIndex={0} aria-expanded={open}
                      onClick={() => toggleGroup(g.groupKey)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(g.groupKey); } }}>
                      <span className="request-group__toggle">
                        {open ? <MdOutlineKeyboardArrowDown size={22} /> : <MdOutlineKeyboardArrowRight size={22} />}
                      </span>
                      {/* header cells share one grid with the asset rows below (see CSS) */}
                      <span className="request-group__requester" role="button" tabIndex={0}
                        title={`${g.requesterName || ''} — view requester details`}
                        onClick={(e) => { e.stopPropagation(); openRequesterPopup(g); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openRequesterPopup(g); } }}>
                        {g.requesterName}
                      </span>
                      <div className="request-group__main">
                        <span className="request-group__title" title={requestGroupTitle(g)}>{requestGroupTitle(g)}</span>
                        <UsageChips usageTypes={g.usageTypes} />
                        <ExpandableText text={g.projectBrief} limit={120} className="request-group__brief" />
                      </div>
                      <div className="request-group__meta">
                        <span className="request-group__col request-group__col--date" title="Requested on">{formatRequestDate(g.groupRequestedDate)}</span>
                        <span className="request-group__col request-group__col--count">{assetCountLabel(g.assetCount)}</span>
                      </div>
                      <span className="request-group__status">
                        <span className={`status-badge ${statusBadgeClass(g.groupStatus)}`}
                          title={g.groupStatus === 'PARTIALLY_APPROVED' ? 'Fully decided: some assets approved, others rejected' : `All assets ${GROUP_STATUS_LABEL[g.groupStatus]?.toLowerCase() || ''}`}>
                          {GROUP_STATUS_LABEL[g.groupStatus] || g.groupStatus}
                        </span>
                      </span>
                    </div>
                    {open && (
                      <div className="request-group__body">
                        <div className="request-row request-row--bar">
                          <span className="request-row__check">
                            <div className="checkbox-wrapper-1 d-inline-block" title="Select every asset of this request">
                              <input id={`select-all-${g.groupKey}`} className="substituted" type="checkbox"
                                checked={allSelected} onChange={(e) => toggleGroupAll(g, e.target.checked)} />
                              <label htmlFor={`select-all-${g.groupKey}`}></label>
                            </div>
                          </span>
                          <label htmlFor={`select-all-${g.groupKey}`} className="request-row__bar-label">Select all assets</label>
                        </div>
                        {g.rows.map((data) => {
                          const size = formatFileSize(data.fileSize);
                          return (
                            <div className="request-row" key={data.requestVideoDownloadId}>
                              <span className="request-row__check">
                                <div className="checkbox-wrapper-1 d-inline-block">
                                  <input id={`checkbox-${data.requestVideoDownloadId}`} className="substituted" type="checkbox"
                                    checked={isSelected(data.requestVideoDownloadId)}
                                    onChange={(e) => toggleRow(g, data.requestVideoDownloadId, e.target.checked)} />
                                  <label htmlFor={`checkbox-${data.requestVideoDownloadId}`}></label>
                                </div>
                              </span>
                              <div className="request-row__main">
                                <button type="button" className='video-list-preview-btn' title="Preview" onClick={() => popupTagShow(data)}>
                                  {String(data.fileTypeId) === "1" ? <FaImage size={12} /> : <FaVideo size={12} />}
                                </button>
                                <div className="request-row__text">
                                  <div className='video-list-title-text'>{data.videoName}</div>
                                  {(data.eventName || data.video_location) && (
                                    <p className='small text-muted mb-0'>
                                      {data.eventName && <>Event: {data.eventName}</>}
                                      {data.eventName && data.video_location && <> · </>}
                                      {data.video_location && <>Location: {data.video_location}</>}
                                    </p>
                                  )}
                                  {data.status === "REJECTED" && data.rejectionReason && (
                                    <p className="request-group__reason">Reason: {data.rejectionReason}</p>
                                  )}
                                  {data.status !== "OPEN" && data.decidedByName && (
                                    <p className='small text-muted mb-0'>
                                      {ROW_STATUS_LABEL[data.status]} by {data.decidedByName}
                                      {data.decidedOn && <> on {moment(Number(data.decidedOn)).format('DD-MM-YYYY')}</>}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className="request-row__meta">
                                {String(data.fileTypeId) === "1" ? 'Image' : 'Video'}
                                {size && <> · {size}</>}
                                {data.videoOrientation === "Horizontal" &&
                                  <MdOutlineStayCurrentLandscape title="Horizontal" size={16} className='video-list-orient-icon' />}
                                {data.videoOrientation === "Vertical" &&
                                  <MdOutlineStayCurrentPortrait title="Vertical" size={16} className='video-list-orient-icon' />}
                              </span>
                              <span className="request-row__status">
                                {/* the control IS the asset's status: its face carries the current
                                    decision in the status colour, opening it changes the decision */}
                                <DecisionMenu status={data.status} disabled={saveFlag} onChange={(v) => onRowStatusChange(data, v)} />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end my-4 page-division">
              <div className="ms-auto d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  Requests per page:
                  <select value={itemsCountPerPage} style={{ minWidth: '60px' }} onChange={(e) => changePerPage(Number(e.target.value))}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                {totalCount > itemsCountPerPage &&
                  <div className='pagination-wrapper m-0 p-0'>
                    <Pagination
                      activePage={activePage}
                      itemsCountPerPage={itemsCountPerPage}
                      totalItemsCount={totalCount}
                      pageRangeDisplayed={5}
                      onChange={(p) => setActivePage(p)}
                    />
                  </div>}
              </div>
            </div>
          </>
        ) : (
          <div className="noData d-flex flex-column">
            <div><img src={noData} alt="noData" className="img-fluid" width="150" height="150" /></div>
            <div>{tab ? `No ${GROUP_STATUS_LABEL[tab].toLowerCase()} requests` : 'No Data Available'}</div>
          </div>
        )}

        <DecisionSelectionBar count={selectedRows.length} busy={saveFlag}
          onApprove={approveSelected} onReject={rejectSelected} onClear={clearSelection} />

        {rejectTarget &&
          <div className="modal-admin-wrapper multiple-upload">
            <div className="modal-ad-popup modal-md reject-dialog">
              <div className="modal-innerpopup">
                <div className="modal-headdiv d-flex justify-content-between">
                  <h5 className="modal-head w-100 text-center">
                    Reject {rejectTarget.ids.length} selected asset{rejectTarget.ids.length === 1 ? '' : 's'}?
                  </h5>
                </div>
                <p className="reject-dialog__names text-center mb-2">{rejectTarget.names.join(' · ')}</p>
                <div className="form_div">
                  <textarea className="editTextInput" id="rejectText" placeholder="Reason for rejection (required)" maxLength={REASON_MAX}
                    value={rejectText} onChange={(e) => setRejectText(e.target.value)} autoFocus
                  ></textarea>
                  <div className="d-flex justify-content-between reject-dialog__hint">
                    <span>The reason is shown to the requester and included in the rejection email.</span>
                    <span>{rejectText.length}/{REASON_MAX}</span>
                  </div>
                </div>
              </div>
              <div className="tag-popup-footer">
                <div className="d-flex align-items-center gap-2">
                  <button type="button" className="accept-all-btn w-100 py-2 accept-all-gray" onClick={() => { setRejectTarget(null); setRejectText(""); }}>
                    Cancel
                  </button>
                  <button type="button" disabled={saveFlag || !rejectText.trim()}
                    className="accept-all-btn w-100 py-2 d-flex align-items-center gap-2 justify-content-center"
                    onClick={confirmReject}>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {requesterPopup &&
          <div className="modal-admin-wrapper">
            <div className="modal-ad-popup modal-md">
              <div className="modal-innerpopup">
                <div className="modal-headdiv d-flex justify-content-between align-items-center">
                  <h5 className="modal-head mb-0">Requester details</h5>
                  <span className="closeBtn"><IoCloseOutline size={32} onClick={() => setRequesterPopup(false)} /></span>
                </div>
                <div className="modal-admin-body my-3">
                  {requesterLoading ?
                    <div className="text-center py-4"><img src={Loader} alt="loading" width="60" height="60" /></div>
                    :
                    <div>
                      {requesterDetails ?
                        <>
                          {/* header: avatar + name + role pill */}
                          <div className="d-flex align-items-center gap-3 pb-3 mb-3" style={{ borderBottom: '1px solid #eef1f3' }}>
                            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#00627B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 600, flexShrink: 0 }}>
                              {(requesterDetails.display_name || '?').trim().charAt(0).toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 16, fontWeight: 600, color: '#1b2733', wordBreak: 'break-word' }}>{requesterDetails.display_name || '—'}</div>
                              {requesterDetails.roleName &&
                                <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 10px', borderRadius: 12, background: '#e6f0f3', color: '#00627B', fontSize: 12, fontWeight: 500 }}>{requesterDetails.roleName}</span>}
                            </div>
                          </div>
                          {/* contact rows */}
                          <div className="d-flex flex-column gap-3">
                            <div className="d-flex align-items-start gap-3">
                              <span style={{ color: '#7b8794', marginTop: 1, flexShrink: 0 }}><MdOutlineEmail size={18} /></span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#98a2ac', textTransform: 'uppercase', letterSpacing: '.03em' }}>Email</div>
                                <div style={{ fontSize: 14, color: '#33414f', wordBreak: 'break-word' }}>{requesterDetails.email_id || '—'}</div>
                              </div>
                            </div>
                            <div className="d-flex align-items-start gap-3">
                              <span style={{ color: '#7b8794', marginTop: 1, flexShrink: 0 }}><MdOutlineLocalPhone size={18} /></span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#98a2ac', textTransform: 'uppercase', letterSpacing: '.03em' }}>Mobile</div>
                                <div style={{ fontSize: 14, color: '#33414f', wordBreak: 'break-word' }}>{requesterDetails.mobile_number ? `${requesterDetails.countryCode ?? ''} ${requesterDetails.mobile_number}`.trim() : '—'}</div>
                              </div>
                            </div>
                            <div className="d-flex align-items-start gap-3">
                              <span style={{ color: '#7b8794', marginTop: 1, flexShrink: 0 }}><MdOutlineBusinessCenter size={18} /></span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#98a2ac', textTransform: 'uppercase', letterSpacing: '.03em' }}>Company</div>
                                <div style={{ fontSize: 14, color: '#33414f', wordBreak: 'break-word' }}>{requesterDetails.companyName || '—'}</div>
                              </div>
                            </div>
                          </div>
                        </>
                        : <div className="text-center py-3" style={{ color: '#98a2ac' }}>User details not found.</div>}

                      {/* #9: this request's project details (mandatory form since 2026-08) */}
                      <div style={{ marginTop: 18, padding: '12px 14px', borderRadius: 8, background: '#f6f8f9', border: '1px solid #eef1f3' }}>
                        <div className="d-flex align-items-center gap-2" style={{ color: '#00627B', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 6 }}>
                          <MdOutlineChatBubbleOutline size={16} /> Request details
                        </div>
                        {(requesterRequest && (requesterRequest.projectName || requesterRequest.projectBrief || requesterRequest.usageTypes)) ?
                          <>
                            <div style={{ color: '#98a2ac', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>Project name</div>
                            <div style={{ color: '#33414f', fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word', marginBottom: 8 }}>{requesterRequest.projectName || '—'}</div>
                            <div style={{ color: '#98a2ac', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>Usage</div>
                            <div style={{ color: '#33414f', fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word', marginBottom: 8 }}>{requesterRequest.usageTypes || '—'}</div>
                            <div style={{ color: '#98a2ac', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>Project brief / purpose</div>
                            <div style={{ color: '#33414f', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{requesterRequest.projectBrief || '—'}</div>
                          </>
                          : <div style={{ color: '#98a2ac', fontSize: 14, fontStyle: 'italic' }}>Not provided (request predates the form)</div>}
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>}
        <MediaPreview
          open={ImagePopup}
          url={popupSelectData.videoUrl}
          fileType={popupSelectData.fileTypeId}
          qualityTypeArray={popupSelectData.qualityTypeArray}
          poster={popupSelectData.thumbnail_image}
          autoPlay
          onClose={cancelPopup}
        />
      </div>
    </>
  );
}

export default VideoDownloadRequest;
